import {
  addToast,
  Button,
  DatePicker,
  Form,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Select,
  SelectItem,
} from "@heroui/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { CalendarDate } from "@internationalized/date";
import { useRef, useState, useEffect } from "react";
import { IconFile } from "@tabler/icons-react";
import { fetchCursos, Cursos } from "@/lib/utils";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { apiFetch } from "@/lib/api";
import { years } from "@/lib/utils";
import { I18nProvider } from "@react-aria/i18n";

export default function CompleteRegistrationModal() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const modal = searchParams.get("modal");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [preview, setPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { data: session, update } = useSession();
  const [cursos, setCursos] = useState<Cursos[]>([]);
  const [selectedCurso, setSelectedCurso] = useState<Cursos | null>(null);
  const [isSelectLoading, setIsSelectLoading] = useState(false);

  const onOpenChange = () => {
    router.push(pathname);
  };

  useEffect(() => {
    if (!session?.accessToken) return;

    setIsSelectLoading(true);
    const loadCursos = async () => {
      const res = await fetchCursos(session.accessToken, setCursos);
      setIsSelectLoading(false);

      if (res !== null) {
        addToast({
          title: res.title,
          description: res.description,
          color: "danger",
        });
      }
    };

    loadCursos();
  }, [session]);

  const handleSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setIsLoading(false);
      return;
    }

    setFileName(file.name);
    setPreview(URL.createObjectURL(file));
    setIsLoading(false);
  };

  const handleImageUpload = async (
    file: File | null,
  ): Promise<string | undefined> => {
    if (!file || file.size === 0) return undefined;

    const fd = new FormData();
    fd.append("imagem", file, file.name);

    const resImage = await apiFetch("/users/me/avatar", {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.accessToken}` },
      body: fd,
    });

    const data = await resImage.json();

    if (resImage.ok) {
      return data.url;
    }

    if (!resImage.ok) {
      addToast({
        title: "Erro ao enviar imagem",
        description: "Não foi possível atualizar a imagem de perfil",
        color: "danger",
      });
      return undefined;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(e.currentTarget);

      // 1) pega o arquivo (se tiver)
      const file = formData.get("imagem") as File | null;

      const imageUrl = await handleImageUpload(file);

      // 2) monta payload do step2 (com imagem opcional)
      const payload: any = {
        curso: formData.get("curso") as string,
        periodo: formData.get("periodo"),
        ano_inicio: formData.get("ano_inicio")
          ? Number(formData.get("ano_inicio"))
          : null,
        ano_fim: formData.get("ano_fim")
          ? Number(formData.get("ano_fim"))
          : null,
        ...(formData.get("data_nascimento") && {
          data_nascimento: formData.get("data_nascimento") as string,
        }),
      };

      if (imageUrl) {
        payload.imagem = imageUrl;
      }

      // 3) step2
      const res = await apiFetch("/auth/register/step2", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        addToast({
          title: "Erro ao completar cadastro",
          description: "Tente novamente mais tarde",
          color: "danger",
        });
        return;
      }

      if (res.ok) {
        // 4) Recarrega a sessao inteira via /users/me
        await update();

        if (session?.user.new) {
          const payload = {
            xp: 50,
          };

          await apiFetch(`/usuarios/${session?.user.id_usuario}/add-xp`, {
            method: "POST",
            headers: { Authorization: `Bearer ${session?.accessToken}` },
            body: JSON.stringify({ xp: 100 }),
          });
        }

        addToast({
          title: "Cadastro completo",
          description:
            "Seu cadastro foi enviado com sucesso! A pagina será recarregada.",
          color: "success",
        });

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
      router.refresh();
      router.push("/");
    } catch (err) {
      console.error(err);
      addToast({
        title: "Erro inesperado",
        description: "Tente novamente mais tarde",
        color: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={modal === "completeRegistration"}
      onOpenChange={onOpenChange}
      backdrop="blur"
      placement="center"
      className="mx-4 md:min-w-1/2"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="text-black">
              Complete o seu cadastro
            </ModalHeader>
            <ModalBody className="overflow-hidden">
              <Form
                className="gap-4 flex-col flex caret-black"
                onSubmit={handleSubmit}
              >
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
                  <div className="flex gap-2 flex-col w-full justify-center items-center max-w-1/2">
                    {preview ? (
                      <Image
                        width={200}
                        height={200}
                        quality={100}
                        src={
                          preview || session?.user?.image || "/defaultUser.jpg"
                        }
                        alt="Preview da imagem de perfil"
                        className="object-cover rounded-full border w-20 h-20"
                      />
                    ) : (
                      <div
                        className={`w-20 h-20 flex items-center justify-center bg-gray-200 rounded-full ${!session?.user.image && "border"}`}
                      >
                        <Image
                          src={session?.user?.image || "/defaultUser.jpg"}
                          width={200}
                          height={200}
                          quality={100}
                          alt="Preview da imagem de perfil"
                          className="object-cover rounded-full border w-20 h-20"
                        />
                      </div>
                    )}
                    <p className="text-black text-sm text-center wrap-break-word whitespace-normal max-w-1/2">
                      {fileName || "Escolha uma imagem"}
                    </p>
                  </div>
                  <Button
                    className="w-full text-black cursor-pointer max-w-[50%]"
                    color="primary"
                    variant="flat"
                    onPress={() => {
                      fileRef.current?.click();
                    }}
                    startContent={<IconFile size={25} />}
                    isLoading={isLoading}
                  >
                    Enviar Imagem
                  </Button>
                  <input
                    ref={fileRef}
                    name="imagem"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleSelectImage}
                  />
                </div>
                <Select
                  name="curso"
                  label="Curso"
                  placeholder="Selecione o curso"
                  isRequired
                  isLoading={isSelectLoading}
                  onSelectionChange={(key) => {
                    const id = Array.from(key)[0];
                    const curso = cursos.find((c) => c.id_curso === id);
                    if (curso) setSelectedCurso(curso);
                  }}
                >
                  {cursos.map((curso) => (
                    <SelectItem key={curso.id_curso}>{curso.nome}</SelectItem>
                  ))}
                </Select>
                <div className="flex gap-3 min-w-full">
                  <Select
                    name="periodo"
                    label="Período"
                    placeholder="Selecione o período"
                    isRequired
                    className="text-black"
                    isDisabled={!selectedCurso}
                  >
                    {selectedCurso?.lista_periodos.map((periodo, id) => (
                      <SelectItem key={id + 1} className="text-black">
                        {periodo}
                      </SelectItem>
                    ))}
                  </Select>
                  <I18nProvider locale="pt-BR">
                    <DatePicker
                      name="data_nascimento"
                      label="Data de nascimento"
                      showMonthAndYearPickers
                      placeholderValue={new CalendarDate(2000, 1, 1)}
                      isRequired
                      isDisabled={!!session?.user?.data_nascimento}
                      variant="bordered"
                    />
                  </I18nProvider>
                </div>
                <div className="flex gap-3 min-w-full">
                  <Select
                    name="ano_inicio"
                    placeholder="Selecione o ano"
                    label="Ano de início"
                    isRequired
                  >
                    {years.map((year) => (
                      <SelectItem key={year.key}>{year.label}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    name="ano_fim"
                    placeholder="Selecione o ano"
                    label="Ano de término"
                    isRequired
                  >
                    {years.map((year) => (
                      <SelectItem key={year.key}>{year.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                <div className="min-w-full flex justify-end gap-2">
                  <Button
                    color="danger"
                    type="button"
                    onPress={onClose}
                    isLoading={isLoading}
                  >
                    Fechar
                  </Button>
                  <Button color="success" type="submit" isLoading={isLoading}>
                    Enviar
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
